import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Lock, RotateCcw, Zap, Smartphone, Check } from 'lucide-react';
import { ExitUpsellModal } from './ExitUpsellModal';
const BUNDLE_URL = 'https://shopdilore.com/cart/54040176230710:1';
type OrderBump = {
  id: 'midnight' | 'nightcap';
  title: string;
  price: number;
  originalPrice: number;
  image: string;
  description: string;
  footnote: string;
};
const ORDER_BUMPS: OrderBump[] = [{
  id: 'midnight',
  title: "Yes! Add The '3 AM Emergency Toolkit' to my order for only $7.99",
  price: 7.99,
  originalPrice: 27,
  image: 'https://cdn.magicpatterns.com/uploads/nf9YkwQhESirDPbhhRrhAu/Order_Bump_1_Image_The_midnight_Rescue_protocol.png',
  description: 'Does the pain ever wake you up in the middle of the night? Don\'t waste an hour tossing and turning. The standard protocol is for your 11 PM reset, but what do you do when a sudden "flare-up" strikes at 3 AM? This Emergency Toolkit gives you a 60-second "Neural Dimmer Switch" to shut down spasms and the "Zero-Gravity" pillow hack to decompress your spine without even getting out of bed. Get back to sleep in minutes, not hours.',
  footnote: 'One-time offer: This 15-Slide digital guide is only available here for $7.99. Normally $27.'
}, {
  id: 'nightcap',
  title: "Wait! Add 'The Anti-Inflammatory Nightcap' for just $7.99",
  price: 7.99,
  originalPrice: 27,
  image: 'https://cdn.magicpatterns.com/uploads/m7eGYqNHuapVoKVTMa3AFa/Order_Bump_2_Image_The_Anti-Inflamatory_Nightcap.png',
  description: 'Movement is only half the battle. If your body is chemically "on fire" with inflammation, no amount of stretching will provide lasting relief. Add this recipe playbook to your order to learn the exact 7-Night Healing Drink Sequence that targets spinal inflammation while you sleep. From the "Golden Spine Latte" to the "Deep Hibernate Tea," these science-backed recipes help chemically turn off the night-fire in your joints and rehydrate your discs.',
  footnote: 'Check this box to unlock 7 powerful recipes for just $7.99. Digital access is instant.'
}];
const BASE_PRICE = 9.87;
export function FinalCTA() {
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [showUpsell, setShowUpsell] = useState(false);
  const toggle = (id: string) => {
    setSelected(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };
  const total = BASE_PRICE + ORDER_BUMPS.reduce((sum, b) => sum + (selected[b.id] ? b.price : 0), 0);
  const baseValue = 90.87;
  const bumpsValue = ORDER_BUMPS.reduce((sum, b) => sum + (selected[b.id] ? b.originalPrice : 0), 0);
  const totalValue = baseValue + bumpsValue;
  // Cart URL changes based on which order bumps are selected.
  // Variant IDs / permalinks provided by the merchant.
  const CART_URLS = {
    none: 'https://www.shopdilore.com/cart/54028549095734:1',
    midnightOnly: 'https://shopdilore.com/cart/54037988147510:1',
    nightcapOnly: 'https://shopdilore.com/cart/54037991031094:1',
    both: 'https://shopdilore.com/cart/54037996208438:1'
  };
  const isMidnight = !!selected.midnight;
  const isNightcap = !!selected.nightcap;
  let checkoutUrl = CART_URLS.none;
  if (isMidnight && isNightcap) checkoutUrl = CART_URLS.both;else if (isMidnight) checkoutUrl = CART_URLS.midnightOnly;else if (isNightcap) checkoutUrl = CART_URLS.nightcapOnly;
  return <section id="final-cta" className="bg-navy-800 py-[96px] px-6 text-center scroll-mt-[56px]" data-id="element-86">
      <div className="max-w-[680px] mx-auto" data-id="element-87">
        <h2 className="font-serif text-white text-[36px] md:text-[52px] font-bold leading-[1.1] mb-5" data-id="element-88">
          Everything. Tonight. For ${total}
        </h2>

        <p className="font-sans text-teal-400 text-[18px] leading-[1.6] mb-12 max-w-[400px] mx-auto" data-id="element-89">
          The protocol. The checklist. The morning guide. The tracker. All of it
          — free with your order.
        </p>

        <a href={checkoutUrl} target="_blank" rel="noopener noreferrer" onClick={e => {
        // If no order bumps selected, intercept and show the exit upsell modal
        if (!isMidnight && !isNightcap) {
          e.preventDefault();
          setShowUpsell(true);
        }
      }} className="inline-flex items-center justify-center w-full max-w-[560px] h-[72px] bg-gold text-navy-800 font-bold text-[16px] md:text-[18px] uppercase rounded-[14px] shadow-[0_8px_40px_rgba(201,168,76,0.4)] hover:brightness-110 hover:scale-[1.02] transition-all" data-id="element-90">
          GET INSTANT ACCESS — ${total} →
        </a>

        <ExitUpsellModal isOpen={showUpsell} onClose={() => setShowUpsell(false)} bundleUrl={BUNDLE_URL} mainOnlyUrl={CART_URLS.none} data-id="element-91" />

        {/* Order Bumps */}
        <div className="mt-8 space-y-5" data-id="element-92">
          {ORDER_BUMPS.map(bump => {
          const isChecked = !!selected[bump.id];
          return <motion.label key={bump.id} htmlFor={`bump-${bump.id}`} animate={isChecked ? {
            boxShadow: '0 0 0 2px rgba(45,212,191,0.6), 0 8px 32px rgba(45,212,191,0.2)'
          } : {
            boxShadow: ['0 0 0 2px rgba(251,191,36,0.4), 0 0 0 0px rgba(251,191,36,0)', '0 0 0 2px rgba(251,191,36,0.9), 0 0 24px 4px rgba(251,191,36,0.35)', '0 0 0 2px rgba(251,191,36,0.4), 0 0 0 0px rgba(251,191,36,0)']
          }} transition={isChecked ? {
            duration: 0.25
          } : {
            duration: 1.8,
            repeat: Infinity,
            ease: 'easeInOut'
          }} className={`block w-full max-w-[560px] mx-auto cursor-pointer rounded-[14px] p-5 md:p-6 text-left transition-colors ${isChecked ? 'bg-teal-400/10 border-2 border-teal-400' : 'bg-yellow-400/10 border-2 border-dashed border-yellow-400'}`} data-id="element-93">
                <input type="checkbox" id={`bump-${bump.id}`} checked={isChecked} onChange={() => toggle(bump.id)} className="sr-only" data-id="element-94" />

                <div className="flex items-start gap-4" data-id="element-95">
                  {/* Checkbox */}
                  <div className={`shrink-0 w-7 h-7 rounded-md border-2 flex items-center justify-center mt-1 transition-colors ${isChecked ? 'bg-teal-400 border-teal-400' : 'bg-white border-yellow-400'}`} data-id="element-96">
                    {isChecked && <Check className="w-5 h-5 text-navy-800" strokeWidth={3} data-id="element-97" />}
                  </div>

                  {/* Image */}
                  <div className="shrink-0 w-20 h-20 md:w-24 md:h-24 rounded-lg overflow-hidden border border-white/10 bg-navy-900" data-id="element-98">
                    <img src={bump.image} alt={bump.title} className="w-full h-full object-cover" data-id="element-99" />
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0" data-id="element-100">
                    <p className={`font-bold text-[15px] md:text-[16px] leading-snug mb-2 ${isChecked ? 'text-teal-300' : 'text-yellow-300'}`} data-id="element-101">
                      ⚠️ {bump.title}
                    </p>
                    <p className="text-white/75 text-[13px] md:text-[14px] leading-relaxed mb-2" data-id="element-102">
                      {bump.description}
                    </p>
                    <p className="text-white/50 text-[12px] italic" data-id="element-103">
                      {bump.footnote}
                    </p>
                  </div>
                </div>
              </motion.label>;
        })}
        </div>

        <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-4 mt-8" data-id="element-104">
          <div className="flex items-center gap-2 text-white/60 text-[12px]" data-id="element-105">
            <Lock className="w-4 h-4 text-teal-400" data-id="element-106" />
            <span data-id="element-107">Secure Checkout</span>
          </div>
          <div className="flex items-center gap-2 text-white/60 text-[12px]" data-id="element-108">
            <RotateCcw className="w-4 h-4 text-teal-400" data-id="element-109" />
            <span data-id="element-110">60-Day Guarantee</span>
          </div>
          <div className="flex items-center gap-2 text-white/60 text-[12px]" data-id="element-111">
            <Zap className="w-4 h-4 text-teal-400" data-id="element-112" />
            <span data-id="element-113">Instant Download</span>
          </div>
          <div className="flex items-center gap-2 text-white/60 text-[12px]" data-id="element-114">
            <Smartphone className="w-4 h-4 text-teal-400" data-id="element-115" />
            <span data-id="element-116">All Devices</span>
          </div>
        </div>

        <p className="font-sans text-gold text-[14px] italic mt-5" data-id="element-117">
          ${totalValue} in total value. Yours today for ${total}
        </p>
      </div>
    </section>;
}